<template>
  <div class="dict-management">
    <div class="page-header">
      <h2>字典管理</h2>
      <el-button type="primary" @click="showTypeDialog">新增字典类型</el-button>
    </div>

    <!-- 字典类型管理 -->
    <el-card class="dict-type-card">
      <div slot="header" class="clearfix">
        <span>字典类型</span>
        <el-button style="float: right; padding: 3px 0" type="text" @click="refreshTypeList">刷新</el-button>
      </div>
      
      <!-- 字典类型表格 -->
      <el-table v-loading="typeLoading" :data="dictTypeList" border style="width: 100%">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="type_name" label="字典名称" />
        <el-table-column prop="type_code" label="字典编码" />
        <el-table-column prop="description" label="描述" />
        <el-table-column prop="data_count" label="数据量" width="100">
          <template slot-scope="scope">
            <el-link type="primary" @click="viewDictData(scope.row)">
              {{ scope.row.data_count || 0 }} 条
            </el-link>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'">
              {{ scope.row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="create_time" label="创建时间" width="180" />
        <el-table-column label="操作" width="200" fixed="right">
          <template slot-scope="scope">
            <div class="table-operations">
              <el-button size="mini" type="text" @click="viewDictData(scope.row)">查看数据</el-button>
              <el-button size="mini" type="text" class="edit-btn" @click="editType(scope.row)">编辑</el-button>
              <el-button size="mini" type="text" class="delete-btn" @click="deleteType(scope.row)">删除</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 字典数据管理 -->
    <el-card class="dict-data-card" v-if="currentDictType">
      <div slot="header" class="clearfix">
        <span>字典数据 - {{ currentDictType.type_name }}</span>
        <div style="float: right;">
          <el-button size="small" @click="showDataDialog">新增数据</el-button>
          <el-button size="small" @click="currentDictType = null">关闭</el-button>
        </div>
      </div>

      <!-- 搜索表单 -->
      <el-form :inline="true" :model="dataSearchForm" class="search-form">
        <el-form-item label="数据标签">
          <el-input v-model="dataSearchForm.data_label" placeholder="请输入数据标签" clearable />
        </el-form-item>
        <el-form-item label="数据值">
          <el-input v-model="dataSearchForm.data_value" placeholder="请输入数据值" clearable />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="dataSearchForm.status" placeholder="请选择状态" clearable>
            <el-option label="禁用" :value="0" />
            <el-option label="启用" :value="1" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="searchData">搜索</el-button>
          <el-button @click="resetDataSearch">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 字典数据表格 -->
      <el-table v-loading="dataLoading" :data="dictDataList" border style="width: 100%">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="data_label" label="数据标签" />
        <el-table-column prop="data_value" label="数据值" />
        <el-table-column prop="data_key" label="数据键" />
        <el-table-column prop="sort_order" label="排序" width="80" />
        <el-table-column prop="status" label="状态" width="100">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'">
              {{ scope.row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" />
        <el-table-column prop="create_time" label="创建时间" width="180" />
        <el-table-column label="操作" width="120" fixed="right">
          <template slot-scope="scope">
            <div class="table-operations">
              <el-button size="mini" type="text" class="edit-btn" @click="editData(scope.row)">编辑</el-button>
              <el-button size="mini" type="text" class="delete-btn" @click="deleteData(scope.row)">删除</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          @size-change="handleDataSizeChange"
          @current-change="handleDataCurrentChange"
          :current-page="dataPagination.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="dataPagination.limit"
          layout="total, sizes, prev, pager, next, jumper"
          :total="dataPagination.total"
        />
      </div>
    </el-card>

    <!-- 字典类型表单对话框 -->
    <el-dialog :title="typeDialogTitle" :visible.sync="typeDialogVisible" width="600px">
      <el-form ref="dictTypeForm" :model="dictTypeForm" :rules="dictTypeRules" label-width="100px">
        <el-form-item label="字典名称" prop="type_name">
          <el-input v-model="dictTypeForm.type_name" />
        </el-form-item>
        <el-form-item label="字典编码" prop="type_code">
          <el-input v-model="dictTypeForm.type_code" :disabled="isEditType" />
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input v-model="dictTypeForm.description" type="textarea" rows="3" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="dictTypeForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input v-model="dictTypeForm.remark" type="textarea" rows="3" />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="typeDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveDictType">确定</el-button>
      </div>
    </el-dialog>

    <!-- 字典数据表单对话框 -->
    <el-dialog :title="dataDialogTitle" :visible.sync="dataDialogVisible" width="600px">
      <el-form ref="dictDataForm" :model="dictDataForm" :rules="dictDataRules" label-width="100px">
        <el-form-item label="数据标签" prop="data_label">
          <el-input v-model="dictDataForm.data_label" />
        </el-form-item>
        <el-form-item label="数据值" prop="data_value">
          <el-input v-model="dictDataForm.data_value" />
        </el-form-item>
        <el-form-item label="数据键" prop="data_key">
          <el-input v-model="dictDataForm.data_key" />
        </el-form-item>
        <el-form-item label="CSS类名" prop="css_class">
          <el-input v-model="dictDataForm.css_class" placeholder="用于显示的CSS类名" />
        </el-form-item>
        <el-form-item label="排序" prop="sort_order">
          <el-input-number v-model="dictDataForm.sort_order" :min="0" :max="999" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="dictDataForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input v-model="dictDataForm.remark" type="textarea" rows="3" />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dataDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveDictData">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getDictTypeList, getDictDataList, createDictType, updateDictType, deleteDictType, createDictData, updateDictData, deleteDictData } from '../../api/system'

export default {
  name: 'DictManagement',
  data() {
    return {
      // 字典类型相关
      typeLoading: false,
      dictTypeList: [],
      typeDialogVisible: false,
      typeDialogTitle: '新增字典类型',
      isEditType: false,
      dictTypeForm: {
        id: null,
        type_name: '',
        type_code: '',
        description: '',
        status: 1,
        remark: ''
      },
      dictTypeRules: {
        type_name: [
          { required: true, message: '请输入字典名称', trigger: 'blur' }
        ],
        type_code: [
          { required: true, message: '请输入字典编码', trigger: 'blur' },
          { pattern: /^[A-Z_]+$/, message: '字典编码只能包含大写字母和下划线', trigger: 'blur' }
        ]
      },
      
      // 字典数据相关
      currentDictType: null,
      dataLoading: false,
      dictDataList: [],
      dataSearchForm: {
        data_label: '',
        data_value: '',
        status: ''
      },
      dataPagination: {
        page: 1,
        limit: 20,
        total: 0
      },
      dataDialogVisible: false,
      dataDialogTitle: '新增字典数据',
      isEditData: false,
      dictDataForm: {
        id: null,
        type_id: null,
        data_label: '',
        data_value: '',
        data_key: '',
        css_class: '',
        sort_order: 0,
        status: 1,
        remark: ''
      },
      dictDataRules: {
        data_label: [
          { required: true, message: '请输入数据标签', trigger: 'blur' }
        ],
        data_value: [
          { required: true, message: '请输入数据值', trigger: 'blur' }
        ]
      }
    }
  },
  created() {
    this.fetchDictTypeList()
  },
  methods: {
    // 字典类型相关方法
    async fetchDictTypeList() {
      this.typeLoading = true
      try {
        const response = await getDictTypeList()
        if (response.code === 200) {
          this.dictTypeList = response.data.list
        }
      } catch (error) {
        this.$message.error('获取字典类型列表失败')
      } finally {
        this.typeLoading = false
      }
    },
    refreshTypeList() {
      this.fetchDictTypeList()
    },
    showTypeDialog() {
      this.typeDialogTitle = '新增字典类型'
      this.isEditType = false
      this.dictTypeForm = {
        id: null,
        type_name: '',
        type_code: '',
        description: '',
        status: 1,
        remark: ''
      }
      this.typeDialogVisible = true
    },
    editType(row) {
      this.typeDialogTitle = '编辑字典类型'
      this.isEditType = true
      this.dictTypeForm = { ...row }
      this.typeDialogVisible = true
    },
    async saveDictType() {
      try {
        await this.$refs.dictTypeForm.validate()
        let response
        if (this.isEditType) {
          response = await updateDictType(this.dictTypeForm)
        } else {
          response = await createDictType(this.dictTypeForm)
        }
        
        if (response.code === 200) {
          this.$message.success(this.isEditType ? '更新成功' : '创建成功')
          this.typeDialogVisible = false
          this.fetchDictTypeList()
        } else {
          this.$message.error(response.msg || '操作失败')
        }
      } catch (error) {
        console.error('保存字典类型失败', error)
      }
    },
    async deleteType(row) {
      try {
        await this.$confirm('确定要删除该字典类型吗？删除后相关的字典数据也会被删除。', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const response = await deleteDictType({ id: row.id })
        if (response.code === 200) {
          this.$message.success('删除成功')
          this.fetchDictTypeList()
          if (this.currentDictType && this.currentDictType.id === row.id) {
            this.currentDictType = null
          }
        } else {
          this.$message.error(response.msg || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败')
        }
      }
    },
    
    // 字典数据相关方法
    viewDictData(row) {
      this.currentDictType = row
      this.fetchDictDataList()
    },
    async fetchDictDataList() {
      if (!this.currentDictType) return
      
      this.dataLoading = true
      try {
        const params = {
          type_id: this.currentDictType.id,
          ...this.dataSearchForm,
          page: this.dataPagination.page,
          limit: this.dataPagination.limit
        }
        const response = await getDictDataList(params)
        if (response.code === 200) {
          this.dictDataList = response.data.list
          this.dataPagination.total = response.data.total
        }
      } catch (error) {
        this.$message.error('获取字典数据列表失败')
      } finally {
        this.dataLoading = false
      }
    },
    searchData() {
      this.dataPagination.page = 1
      this.fetchDictDataList()
    },
    resetDataSearch() {
      this.dataSearchForm = {
        data_label: '',
        data_value: '',
        status: ''
      }
      this.dataPagination.page = 1
      this.fetchDictDataList()
    },
    handleDataSizeChange(val) {
      this.dataPagination.limit = val
      this.fetchDictDataList()
    },
    handleDataCurrentChange(val) {
      this.dataPagination.page = val
      this.fetchDictDataList()
    },
    showDataDialog() {
      this.dataDialogTitle = '新增字典数据'
      this.isEditData = false
      this.dictDataForm = {
        id: null,
        type_id: this.currentDictType.id,
        data_label: '',
        data_value: '',
        data_key: '',
        css_class: '',
        sort_order: 0,
        status: 1,
        remark: ''
      }
      this.dataDialogVisible = true
    },
    editData(row) {
      this.dataDialogTitle = '编辑字典数据'
      this.isEditData = true
      this.dictDataForm = { ...row }
      this.dataDialogVisible = true
    },
    async saveDictData() {
      try {
        await this.$refs.dictDataForm.validate()
        let response
        if (this.isEditData) {
          response = await updateDictData(this.dictDataForm)
        } else {
          response = await createDictData(this.dictDataForm)
        }
        
        if (response.code === 200) {
          this.$message.success(this.isEditData ? '更新成功' : '创建成功')
          this.dataDialogVisible = false
          this.fetchDictDataList()
        } else {
          this.$message.error(response.msg || '操作失败')
        }
      } catch (error) {
        console.error('保存字典数据失败', error)
      }
    },
    async deleteData(row) {
      try {
        await this.$confirm('确定要删除该字典数据吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const response = await deleteDictData({ id: row.id })
        if (response.code === 200) {
          this.$message.success('删除成功')
          this.fetchDictDataList()
        } else {
          this.$message.error(response.msg || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败')
        }
      }
    }
  }
}
</script>

<style scoped>
.dict-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.dict-type-card {
  margin-bottom: 20px;
}

.dict-data-card {
  margin-top: 20px;
}

.search-form {
  background: #f5f5f5;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

.clearfix:before,
.clearfix:after {
  display: table;
  content: "";
}

.clearfix:after {
  clear: both;
}

.table-operations {
  display: flex;
  gap: 4px;
  justify-content: center;
  flex-wrap: nowrap;
}

.table-operations .el-button--text {
  padding: 2px 6px;
  font-size: 12px;
  border: none;
  background: none;
  white-space: nowrap;
  min-width: unset;
}

.table-operations .edit-btn {
  color: #409eff;
}

.table-operations .edit-btn:hover {
  color: #66b1ff;
  background-color: #ecf5ff;
}

.table-operations .delete-btn {
  color: #f56c6c;
}

.table-operations .delete-btn:hover {
  color: #f78989;
  background-color: #fef0f0;
}
</style>